<template>
    <div @click.stop="" class="root">

        <button
            type="button" class="minus" @mousedown.stop="changeValue('minus')" @mouseup="removeInterval"
            @contextmenu.stop.prevent
        >
            <!-- – -->
            <svg width="13px" fill="currentColor" viewBox="0 0 79 79" xmlns="http://www.w3.org/2000/svg">
                <path d="M 78.31 47.98 L 0.74 47.98 L 0.74 42.63 L 78.31 42.63 Z" />
            </svg>
        </button>

        <div class="amount" :class="{ 'notNull': inputValue !== 0 }">
            <input ref="amountInput" type="text" inputmode="numeric" v-model="inputValue" @click.stop="select($event)"
                @keydown="handleKeydown" @keydown.space.prevent>
        </div>

        <button
            type="button" class="plus" @mousedown.stop="changeValue('plus')" @mouseup="removeInterval"
            @contextmenu.stop.prevent
        >
            <!-- + -->
            <svg width="13px" viewBox="0 0 79 79" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M 36.48 78.91 L 36.48 42.37 L 0.01 42.37 L 0.01 36.36 L 36.48 36.36 L 36.48 -0.04 L 42.34 -0.04 L 42.34 36.36 L 78.96 36.36 L 78.96 42.37 L 42.34 42.37 L 42.34 78.91 Z"
                />
            </svg>
        </button>

    </div>
</template>

<script setup lang="ts">
import { InputCustomEvent } from "@ionic/vue";
import { computed, ref, onMounted, onBeforeUnmount } from "vue";

import { INITIAL_TIMER, TIMER_MIN, TIMER_SUBSTRACTION } from "@/assets/variables";

// import { ION_VARIANT } from "@/types/enum/ionicEnum";
import { InputAmountPerformanceProps } from "@/types/props";
import { InputAmountEmits } from "@/types/emits";
// import { appResult } from "@/functions/enviroment";

const props = withDefaults(defineProps<InputAmountPerformanceProps>(), {
    min: 0,
    amountStep: 1
})
const emit = defineEmits<InputAmountEmits>()

const amountInput = ref()

let intervalId: ReturnType<typeof setTimeout>;
let changeTimer = INITIAL_TIMER;

let preventBlur = false

const inputValue = computed<number>({
    get: () => {
        if (props.modelValue === undefined) return 0
        return Number(props.modelValue)
    },
    set: async (value) => {
        // const convertedNumber = Number(value)
        const convertedNumber = Number(String(value).replace(/[^0-9]*/g, ''));

        if (isNaN(convertedNumber) || convertedNumber < props.min) {
            emit('update:modelValue', props.min)
            amountInput.value.value = props.min;
            return
        }

        if (props.max && convertedNumber > props.max) {
            emit('update:modelValue', props.max)
            amountInput.value.value = props.max;
            return
        }

        // if (amountInput.value?.value.length === 0) {
        //     amountInput.value.value = 0
        // }

        // // remove '0' at the beginnig: 025 -> 25
        // if (amountInput.value?.value.length <= 2 && +amountInput.value.value[0] === 0) {
        //     amountInput.value.value = convertedNumber;
        // }

        amountInput.value.value = convertedNumber;

        emit('update:modelValue', convertedNumber)
        emit('change', convertedNumber)
    },
})

const select = (event: InputCustomEvent): void => {
    if (event.target.select) { // tapped directly on html input
        preventBlur = true
        event.target.select();
    } else if (event.target.children[0].select) { // tapped on the wrapper
        preventBlur = true
        event.target.children[0].select();
    }
}

const removeInterval = () => {
    clearInterval(intervalId)
    changeTimer = INITIAL_TIMER
}

const setUpdateInterval = (modifier: 'plus' | 'minus') => {
    clearInterval(intervalId)
    changeTimer = changeTimer - TIMER_SUBSTRACTION >= TIMER_MIN ? changeTimer - TIMER_SUBSTRACTION : TIMER_MIN
    intervalId = setTimeout(() => {
        inputValue.value += modifier === 'plus' ? props.amountStep : (-1 * props.amountStep)
        setUpdateInterval(modifier)
    }, changeTimer)
}

const changeValue = (modifier: 'plus' | 'minus'): void => {
    if (modifier === 'minus' && inputValue.value === 0) return;
    inputValue.value += modifier === 'plus' ? props.amountStep : (-1 * props.amountStep)

    setUpdateInterval(modifier)
}

const handleKeydown = (e: KeyboardEvent) => {
    if (!isNaN(Number(e.key)) && Number(e.key) >= 0 && Number(e.key) <= 9 || e.key === 'Backspace') {
        // do default
    } else {
        e.preventDefault();
    }
}

onMounted(() => {
  document.addEventListener('mouseup', removeInterval)
})

onBeforeUnmount(() => {
  document.removeEventListener('mouseup', removeInterval)
})
</script>

<style scoped>
.root {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    /* flex-direction: row-reverse; */
    margin: 0 14px;
    cursor: auto;
}

.plus,
.minus {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
    background-color: #f5f5f5;
    color: gray;
    outline: none;
}

.plus:hover,
.minus:hover {
    cursor: pointer;
    filter: brightness(1.1);
}

.plus:active,
.minus:active {
    /* border: 1px solid var(--ion-color-pink); */
    /* color: var(--ion-color-pink); */
    filter: unset;
    box-shadow: 0 0px 1px -2px rgba(0, 0, 0, 0.2), 0 0px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
}

.amount {
    color: black;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    /* font-size: 14px; */
    z-index: 1;
    /* background-color: #d1d1d1; */
    /* background-color: #e9e9e9; */
    /* background-color: #f3f3f8; */
    background-color: var(--ion-color-grey4);
    transition: 0.2s linear;
    /* background-color: var(--ion-color-pink); */
}

.amount input {
    width: 36px;
    width: 4ch;
    padding: 0;
    background-color: transparent;
    border: none;
    outline: none;
    text-align: center;
}

.amount input::selection {
    background-color: hsl(335, 100%, 70%);
    color: white;
}

.amount input::-moz-selection {
    background-color: hsl(335, 100%, 70%);
}

.amount.notNull {
    background-color: var(--ion-color-pink);
    color: white;
}

/* .clicked {
    background: linear-gradient(145deg, #cacaca, #f0f0f0);
} */
</style>
